import { ReportDefinition } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';

type OpenFolioRow = {
   rowId: string;
   roomNumber: string;
   name: string;
   confirmationNumber: string;
   balance: string;
   balanceRaw: number;
   hasRealTyallsRowId: boolean;
};

function asString(value: any): string {
   if (value === null || value === undefined) return '';
   return String(value);
}

function parseReportJson(reportJson: any): any {
   if (typeof reportJson !== 'string') return reportJson;

   try {
      return JSON.parse(reportJson);
   } catch {
      return {};
   }
}

function toNumberOrNull(value: any): number | null {
   if (value === null || value === undefined || value === '') return null;

   if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
   }

   const cleaned = String(value)
      .replace(/,/g, '')
      .replace(/\s+/g, '')
      .replace(/^-\s*/, '-');

   const amount = Number(cleaned);
   return Number.isFinite(amount) ? amount : null;
}

function formatCurrency(value: number): string {
   const formatted = Math.abs(value).toLocaleString('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
   });

   return value < 0 ? `-$${formatted}` : `$${formatted}`;
}

function getRealTyallsRowId(row: any): string {
   return asString(row?.tyallsRowId ?? row?.TyallsRowId);
}

function getRowId(row: any): string {
   const realTyallsRowId = getRealTyallsRowId(row);
   if (realTyallsRowId) return realTyallsRowId;

   return (
      asString(row?.Id ?? row?.id) ||
      `open-folio:${asString(row?.RoomNumber)}:${asString(
         row?.ConfirmationNumber
      )}:${asString(row?.Name)}`
   );
}

function getSourceRows(reportJson: any): any[] {
   const parsed = parseReportJson(reportJson);

   if (Array.isArray(parsed?.OpenFolios)) {
      return parsed.OpenFolios;
   }

   if (Array.isArray(parsed?.Rows)) {
      return parsed.Rows;
   }

   if (Array.isArray(parsed?.rows)) {
      return parsed.rows;
   }

   return [];
}

function normaliseRows(reportJson: any): OpenFolioRow[] {
   return getSourceRows(reportJson)
      .map((row) => {
         const balanceRaw = toNumberOrNull(row?.Balance) ?? 0;

         return {
            rowId: getRowId(row),
            roomNumber: asString(row?.RoomNumber),
            name: asString(row?.Name),
            confirmationNumber: asString(row?.ConfirmationNumber),
            balance: formatCurrency(balanceRaw),
            balanceRaw,
            hasRealTyallsRowId: !!getRealTyallsRowId(row)
         };
      })
      .filter((row) => row.roomNumber || row.name || row.confirmationNumber);
}

function sortRows(rows: OpenFolioRow[]): OpenFolioRow[] {
   return [...rows].sort((a, b) => {
      const aRoom = Number(a.roomNumber);
      const bRoom = Number(b.roomNumber);

      if (Number.isFinite(aRoom) && Number.isFinite(bRoom)) {
         return aRoom - bRoom;
      }

      return a.roomNumber.localeCompare(b.roomNumber);
   });
}

export const OPEN_FOLIOS_DEFINITION: ReportDefinition = {
   reportType: 'OpenFolios',
   version: 2,
   title: 'Open Folios',

   sorting: {
      enabled: true,
      persist: true
   },

   header: {
      title: 'Open Folios',
      snippets: ({ allRows }) => {
         const dataRows = allRows.filter(
            (row) => row.cells?.['rowType'] === 'openFolio'
         );

         const totalBalance = dataRows.reduce((total, row) => {
            const amount = Number(row.cells?.['balanceRaw']);
            return Number.isFinite(amount) ? total + amount : total;
         }, 0);

         return [
            {
               label: 'Count',
               value: dataRows.length
            },
            {
               label: 'Total Balance',
               value: formatCurrency(totalBalance),
               tone: totalBalance < 0 ? 'danger' : 'neutral'
            }
         ];
      }
   },

   rowChecks: {
      enabled: false,
      includeNestedRows: false,
      selectableRowTypes: ['openFolio']
   },

   annotations: {
      enabled: true,
      selectableRowTypes: ['openFolio'],
      disabledMessage:
         'Row comments and row highlighting are not available for this report layout. Please use the main report comments panel to add comments for this report.'
   },

   columns: [
      {
         key: 'roomNumber',
         header: 'ROOM',
         width: '100px',
         sort: { type: 'string' }
      },
      {
         key: 'name',
         header: 'NAME',
         width: '360px',
         maxChars: 120,
         sort: { type: 'string' }
      },
      {
         key: 'confirmationNumber',
         header: 'CONFIRMATION',
         width: '160px',
         sort: { type: 'string' }
      },
      {
         key: 'balance',
         header: 'BALANCE',
         width: '140px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['balanceRaw'],
            defaultDirection: 'desc'
         }
      }
   ],

   filter: {
      fields: [
         { key: 'roomNumber', label: 'Room', type: 'string' },
         { key: 'name', label: 'Name', type: 'string' },
         {
            key: 'confirmationNumber',
            label: 'Confirmation Number',
            type: 'string'
         },
         {
            key: 'balance',
            label: 'Balance',
            type: 'number',
            getValue: (row) => row.cells['balanceRaw']
         }
      ]
   },

   flattenRows: (reportJson: any): ReportRowVm[] => {
      const rows = sortRows(normaliseRows(reportJson));

      return rows.map((row) => ({
         rowId: row.rowId,
         isAnnotatable: row.hasRealTyallsRowId,
         cells: {
            rowType: 'openFolio',
            roomNumber: row.roomNumber,
            name: row.name,
            confirmationNumber: row.confirmationNumber,
            balance: row.balance,
            balanceRaw: row.balanceRaw
         }
      }));
   }
};
